import { ObjectId } from 'mongodb'
import { UserType, Gender, MaritalStatus, AccountStatus, PendencyStatus } from '@mutual-services/account-types'
import { ScoreLetterFull, ScoreLetter, LoanRequestOption } from '@mutual-libs/types/service-types/credit-score'
import { DbType } from '@mutual-libs/shared-types'

export enum AwsEventType {
  s3 = 's3',
  apiGatewayAwsProxy = 'apiGatewayAwsProxy',
  sqsEvent = 'sqsEvent',
  other = 'other'
}

export interface PagerParams {
  limit: number,
  offset: number
}

export interface FilteredResult<T> {
  data: T[],
  matchCount: number
}

export enum LoanPaymentStatus {
  REGULAR = 'regular',
  LATE = 'late',
  LEGAL_COLLECTION = 'legalCollection',
  PAID = 'paid',
  RETURNED = 'returned'
}

export enum InstallmentStatus {
  unpaid = 'unpaid',
  paid = 'paid',
  partiallyPaid = 'partiallyPaid'
}

export enum TargetType {
  borrower = 'borrower',
  investor = 'investor'
}

export enum LoanStatus {
  open = 'open',
  analysis = 'analysis',
  expired = 'expired',
  rejected = 'rejected',
  approved = 'approved',
  paid = 'paid',
  late = 'late'
}

export interface NotificationVariables {
  // eslint-disable-next-line camelcase
  nome_tomador?: string,
  // eslint-disable-next-line camelcase
  nome_investidor?: string,
  // eslint-disable-next-line camelcase
  dia_de_hoje?: string,
  // eslint-disable-next-line camelcase
  numero_parcela?: number,
  // eslint-disable-next-line camelcase
  link_boleto?: string,
  // eslint-disable-next-line camelcase
  data_promessa?: string,
  dueDate?: string,
  invoiceUrl?: string,
  fine?: string,
  interestMonth?: string,
  installmentAmount?: string,
  installmentNumber?: string,
  installmentsCount?: string,
  url?: string,
  scheduledPaymentDate?: string
}

export interface CollectionNotification {
  _id?: string | ObjectId,
  targetId: string,
  installment: Installment,
  targetType: TargetType,
  notificationType: string,
  variables: NotificationVariables,
  sentAt: Date,
  subjectId?: string | ObjectId
}

export interface Installment {
  _id?: string | ObjectId,
  loanId: string,
  borrowerId: string,
  investorId: string,
  status: InstallmentStatus,
  late: boolean,
  dueDate: Date,
  number: number,
  initialAmountDueCents: number,
  amountPaidCents: number,
  overPaid: boolean,
  createdAt: Date,
  updatedAt: Date,
  deleted: boolean,
  renewedAt?: Date,
  renewedUntil?: Date,
  paymentDate?: Date,
  scheduledPaymentDate: Date | null
}

export interface InstallmentWithAccountData extends Installment {
  borrower?: any,
  investor?: any
}

export interface Location {
  speed: number,
  heading: number,
  accuracy: number,
  longitude: number,
  altitude: number,
  latitude: number,
  altitudeAccuracy?: number
}

export interface Address {
  cep: string,
  street: string,
  number: string,
  complement?: string,
  neighborhood: string,
  city: string,
  state: string
}

// TODO: english?
export enum BankAccountType {
  corrente = 'cc',
  poupanca = 'cp'
}

export interface Bank {
  accountType: BankAccountType,
  account: string,
  code: string,
  agency: string
}

export interface PJUserData {
  cnpj: string,
  responsibleCpf: string,
  name: string,
  responsibleName: string
}

export interface PFUserData {
  cpf: string
}

export interface UserData {
  userId: string,
  type: string, // PF or PJ
  email: string,
  address: Address,
  pjUserData?: PJUserData,
  pfUserData?: PFUserData,
  bank: Bank,
  phone: string,
  name: string
}

export interface LegacyAccount {
  _id: string,
  name: string,
  nickname: string
}

export interface BasePipeSeller {
  pipedriveId: number,
  name: string,
  email: string,
  phone: string | null
}

export interface PipeSeller extends BasePipeSeller {
  _id: string,
  createdAt: Date,
  updatedAt: Date
}

export interface SegmentTrackData {
  userId: string,
  event: string,
  properties: any
}

export enum TrackEventType {
  registered = 'Registered',
  registeredInvestor = 'RegisteredInvestor',
  registeredBorrower = 'RegisteredBorrower',
  borrowerHasLimit = 'BorrowerHasLimit',
  userHasLimit = 'UserHasLimit',
  borrowerNoLimit = 'BorrowerNoLimit',
  initialCreateLoan = 'InitialCreateLoan',
  bankConfirm = 'BankConfirm',
  addressOwnershipConfirm = 'AddressOwnershipConfirm',
  personalConfirm = 'PersonalConfirm',
  borrowerApproved = 'BorrowerApproved',
  investorApproved = 'InvestorApproved',
  makeInvestment = 'MakeInvestment',
  borrowerReceivedInvestment = 'BorrowerReceivedInvestment',
  requestLoan = 'RequestLoan',
  publishLoan = 'PublishLoan',
  borrowerPublishRejected = 'BorrowerPublishRejected',
  borrowerLoanPaid = 'BorrowerLoanPaid',
  investorLoanPaid = 'InvestorLoanPaid',
  pendingBackoffice = 'PendingBackoffice',
  chargedWallet = 'ChargedWallet',
  generateBoleto = 'GenerateBoleto',
  eventTest = 'EventTest',
  borrowerLoanEffected = 'BorrowerLoanEffected',
  investorLoanEffected = 'InvestorLoanEffected',
  investorUpsell = 'InvestorUpsell',
  expiredLoan = 'expiredLoan',
  pendencyRejected = 'PendencyRejected',
  borrowerPaidInstallment = 'BorrowerPaidInstallment',
  investorReceivedInstallmentSlice = 'InvestorReceivedInstallmentSlice',
  borrowerInBlacklist = 'BorrowerInBlacklist',
  investorInBlacklist = 'InvestorInBlacklist',
  userInFraudList = 'UserInFraudList',
  investorsOnMutualInvest = 'InvestorsOnMutualInvest',
  investorDealGain = 'InvestorDealGain',
  borrowerHasSimulated = 'borrowerHasSimulated',
  effectedLoan = 'effectedLoan',
  smallBusinessEffectedLoan = 'smallBusinessEffectedLoan',
  borrowerAnswers = 'BorrowerAnswers',
  borrowerManuallyApproved = 'borrowerManuallyApproved',
  manualFlowFinalizedByAnalyst = 'manualFlowFinalizedByAnalyst',
  manualFlowOnDeniedList = 'manualFlowOnDeniedList',
  manualFlowOnApprovedList = 'manualFlowOnApprovedList',
  loanLateDays = 'loanLateDays',
  loanUpdatePaymentStatusToRegular = 'loanUpdatePaymentStatusToRegular',
  userApprovedWithdrawallRequest = 'userApprovedWithdrawallRequest',
  borrowerRejectedLoan = 'borrowerRejectedLoan',
  borrowerRenegotiation = 'borrowerRenegotiation',
  borrowerRequestAnalyzeScore = 'borrowerRequestAnalyzeScore',
  activeAutomaticInvestment = 'activeAutomaticInvestment',
  inactiveAutomaticInvestment = 'inactiveAutomaticInvestment',
  onboardPj = 'onboard_pj',
  loanFraud = 'loanFraud',
  pjPendencySent = 'pj_pendency_sent',
  pjDocSent = 'pj_doc_sent',
  pjAccountCreated = 'pj_account_created',
  pjCompleteAccountPendencies = 'dados_pj',
  partnerManagementPartnerRegistered = 'partner_management_partner_registered',
  partnerManagementFirstSimulation = 'partner_management_first_simulation',
  partnerManagementAvailableWithdrawal = 'partner_management_available_withdrawal'
}

export enum TrackTarget {
  SEGMENT = 'SEGMENT',
  RD_STATION = 'RD_STATION',
  PIPEDRIVE = 'PIPEDRIVE',
  APPSFLYER = 'APPSFLYER'
}

export type TrackMethod = (id: string, event: TrackEventType, properties?: TrackProperties) => Promise<any>
export type IdentifyMethod = (id: string, properties?: TrackProperties) => Promise<boolean>

export interface TrackProperties { [key: string]: any }

export type TrackEventsMap = {
  [key in TrackEventType]: TrackEvent
}

export type TargetsMap = {
  [key in TrackTarget]: {
    trackMethod: TrackMethod,
    identifyMethod: IdentifyMethod
  }
}

export interface TrackEvent {
  shouldIdentify: boolean,
  targets: TrackTarget[]
}

export interface TrackEventData {
  accountId: string,
  event: TrackEventType,
  properties?: TrackProperties
}

// TODO: Merge this interface with CustomFields
export interface IdentifyData {
  email: string,
  firstName: string,
  lastName: string,
  phone: string,
  userType: UserType,
  fullName: string | null,
  nickname: string,
  cpf: string,
  profilePicture: string | null,
  age: number | null,
  birthdate: string | null,
  gender: Gender | null,
  maritalStatus: MaritalStatus | null,
  accountStatus: AccountStatus,
  addressStatus: PendencyStatus,
  addressProofStatus: PendencyStatus,
  bankStatus: PendencyStatus,
  idDocumentFrontStatus: PendencyStatus,
  idDocumentBackStatus: PendencyStatus,
  personalInformationStatus: PendencyStatus,
  selfieStatus: PendencyStatus,
  cep: string | null,
  city: string | null,
  neighborhood: string | null,
  street: string | null,
  state: string | null,
  score: string | null,
  numericScore?: number | null,
  monthlyLimitCents?: number | null,
  accountId: string
}

export interface FieldValue {
  contact: string,
  field: string,
  value: string
}

export enum FieldType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  DATE = 'date',
  DROPDOWN = 'dropdown',
  MULTISELECT = 'multiselect',
  RADIO = 'radio',
  CHECKBOX = 'checkbox',
  HIDDEN = 'hidden',
  CURRENCY = 'currency',
  NUMBER = 'number'
}

export enum FieldOptions {
  DROPDOWN = 'dropdown',
  MULTISELECT = 'multiselect',
  RADIO = 'radio',
  CHECKBOX = 'checkbox'
}

/* RD Station Types */
export interface RdAuth {
  clientId: string,
  clientSecret: string,
  refreshToken: string
}

export interface BaseRdToken {
  accessToken: string,
  expiresIn: number,
  refreshToken: string
}

export interface RdToken {
  _id: string,
  clientId: string,
  accessToken: string,
  expiresIn: number,
  refreshToken: string,
  updatedAt: Date
}

export enum RdEventType {
  CONVERSION = 'CONVERSION'
}

export interface RdEventPayload extends BaseRdContact {
  conversionIdentifier: TrackEventType
}

export enum RdEventFamily {
  CDP = 'CDP'
}

export interface RdEvent {
  eventType: RdEventType,
  eventFamily: RdEventFamily.CDP,
  payload: RdEventPayload
}

export interface RdEventResponse {
  eventUuid: string
}

export interface RdContactCustomFields {
  cfAccountid: string,
  cfCep: string | null,
  cfNeighborhood: string | null,
  cfStreet: string | null,
  cfNickname: string | null,
  cfGender: string | null,
  cfMaritalStatus: string | null,
  cfUserType: string | null,
  cfStatus: string | null,
  cfCpf: string | null,
  cfBirthdate: Date | string | null,
  cfAge: string | null,
  cfAccountStatus: string | null,
  cfAddressStatus: string | null,
  cfAddressProofStatus: string | null,
  cfBankStatus: string | null,
  cfIdDocumentFrontStatus: string | null,
  cfIdDocumentBackStatus: string | null,
  cfPersonalInformationStatus: string | null,
  cfSelfieStatus: string | null,
  cfRegisteredAtDay: string | null,
  cfRegisteredAtMonth: string | null,
  cfRegisteredAtYear: string | null,
  cfRegisteredAtFull: string | null,
  cfPipedriveOwnerId: string | null,
  cfInvestorProfile: InvestorProfilePTBR | null,
  cfDaysWithoutActivation: string | null,
  cfDaysWithoutPositiveActivation: string | null,
  cfLateDays: string | null,
  cfFormOfEmployment: string | null,
  cfEmploymentSector: string | null,
  cfWithdrawallRequestAmountCents: string | null,
  cfUserAnalyzeScoreResult: string | null,
  cfDocumentRejectReason: string | null,
  cfLoanBestPayDay: string | null,
  cfScoreLimit: string | null,
  cfInstallmentPaidValue: string | null,
  cfInstallmentPaidDate: string | null
}

export interface RdPjContactCustomField {
  cfCfPjCnpj: string | null,
  cfCfPjRequestedamount: string | null,
  cfCfPjNoofinstallments: string | null,
  cfCfPjLoandescription: string | null,
  cfCfPjMonthlyincome: string | null,
  cfCfPjAddress: string | null,
  cfCfPjComplement: string | null,
  cfCfPjCity: string | null,
  cfCfPjState: string | null,
  cfCfPjCep: string | null,
  cfCfPjInstagram: string | null,
  cfCfPjFacebook: string | null,
  cfCfPjCompanymail: string | null,
  cfCfPjSite: string | null,
  cfCfPjRepresentativename: string | null,
  cfCfPjRepresentativecpf: string | null,
  cfCfPjNeighborhood: string | null,
  cfCfPjStreetnumber: string | null,
  cfCfPjLinkedin: string | null,
  cfCfPjHasmutualaccount: string | null,
  cfPjscorequantifoundingage: string | null,
  cfPjscorequantiqualifypartnershipentrydate: string | null,
  cfPjscorequantiactivities: string | null,
  cfPjscorequantiscorepfandpj: string | null,
  cfPjscorequantiindebtednessbehavior: string | null,
  cfPjscorequantiindebtednesstype: string | null,
  cfPjscorequantirestrictions: string | null,
  cfPjscorequantihspnserasarestriction: string | null,
  cfPjhpj4score: string | null,
  cfPjhspnscore: string | null,
  cfPjhspnscoredocument: string | null,
  cfPjanalysisstatus: string | null,
  cfPjanalysisstatusmotive: string | null,
  cfPjcompanyofficialname: string | null,
  cfPjcompanytradename: string | null,
  cfPjcompanyfoundeddate: string | null,
  cfPjcompanytaxidstatus: string | null,
  cfPjcompanycapital: string | null,
  cfPjcompanytaxregime: string | null,
  cfPjcompanylegalnature: string | null,
  cfOriginPj: string | null
}

export enum InvestorProfilePTBR {
  ARROJADO = 'Arrojado',
  MODERADO = 'Moderado',
  CONSERVADOR = 'Conservador'
}

export interface BaseRdContact extends Partial<RdContactCustomFields>, Partial<RdPjContactCustomField> {
  email: string,
  name?: string | null,
  bio?: string | null,
  jobTitle?: string | null,
  linkedn?: string | null,
  facebook?: string | null,
  state?: string | null,
  city?: string | null,
  country?: string | null,
  twitter?: string | null,
  personalPhone?: string | null,
  mobilePhone?: string | null,
  website?: string | null,
  tags?: string[]
}

export interface RdContact extends BaseRdContact {
  uuid: string
}

export enum PipedriveDealFields {
  score = 'f314dd728201daab0df631a7bd6cd0c74780948e',
  montlhyLimit = 'b026888dd05075f7261a3b89cb95a69c2b161c67',
  totalLimitIn18x = 'bfe8eddb6962cc47dcc8823d155835560be92782',
  totalLimitIn12x = '58f526f9849c9b5c51a2745958ca7ab894106ef1',
  requestedAmount = '3d4bd1bb14ade6aaf4d1bc754bdf150ef61d561d',
  installmentAmount = 'cc86fb342a7de88dcf5abde0f91a9f7b3f7c0bdc',
  interestPercentagePerMonth = 'c2b851081a6989a05f9905cd6947063c07bb850c',
  cetPercentagePerYear = '475c4288097ae28576dd2cb804753cc9d08569d8',
  paidBilletValue = 'fba21ddc6549c8579de8bcbdaef1df2ade58181e',
  generateBilletValue = '13bdab245731c41cfee188518a0f61fe465d3ab1'
}

export enum PipedrivePersonFields {
  whatsappUrl = '2a33a419d81dbb0d8dc3c35769cc77fb51ca59da'
}

export enum PipedriveUserId {
  FirstSeller = 6668911, // Kaike Souza
  SecondSeller = 11324955, // Phelipe Fernandes
  ThirdSeller = 10058561 // Ana Clara
}

export enum PipedriveStatus {
  WON = 'won',
  LOST = 'lost',
  DELETED = 'deleted',
  OPEN = 'open'
}

export interface PipedrivePerson {
  id: number,
  companyId: number,
  ownerId: {
    id: number,
    name: string,
    email: string,
    hasPic: boolean,
    picHash: string | null,
    activeFlag: boolean,
    value: number
  },
  orgId: number | null,
  name: string,
  firstName: string,
  lastName: string,
  openDealsCount: number,
  relatedOpenDealsCount: number,
  closedDealsCount: number,
  relatedClosedDealsCount: number,
  participantOpenDealsCount: number,
  participantClosedDealsCount: number,
  emailMessagesCount: number,
  activitiesCount: number,
  doneActivitiesCount: number,
  undoneActivitiesCount: number,
  referenceActivitiesCount: number,
  filesCount: number,
  notesCount: number,
  followersCount: number,
  wonDealsCount: number,
  relatedWonDealsCount: number,
  lostDealsCount: number,
  relatedLostDealsCount: number,
  activeFlag: boolean,
  phone: {
    label: string,
    value: string,
    primary: boolean
  }[],
  email: {
    label: string,
    value: string,
    primary: boolean
  }[],
  firstChar: string,
  updateTime: string,
  addTime: string,
  visibleTo: string,
  pictureId: {
    itemType: string,
    itemId: number,
    activeFlag: boolean,
    addTime: string,
    updateTime: string,
    addedByUserId: number,
    pictures: { [key: string]: string },
    value: number
  },
  nextActivityDate: string | null,
  nextActivityTime: string | null,
  nextActivityId: string | null,
  lastActivityId: string | null,
  lastActivityDate: string | null,
  lastIncomingMailTime: string | null,
  lastOutgoingMailTime: string | null,
  label: string | null,
  orgName: string | null,
  ccEmail: string,
  [PipedrivePersonFields.whatsappUrl]?: string
}

export enum MutualPartnerEnum {
  APP_RENDA_FIXA = 'appRendaFixa',
  EDUCANDO_SEU_BOLSO = 'educandoSeuBolso',
  CREDI_HOME = 'crediHome',
  MUTUAL_MARKETING = 'mutualMarketing'
}

export interface ScoreDataParams {
  numberOfInstallments: number,
  bestPayDay: number,
  requestedAmountCents: number,
  hasDefaulted: string,
  wasDefaulted: string,
  primaryIncome: number,
  motive: string
}

export interface RegisterLeadAndCalculateScoreInput {
  token: string,
  cpf: string,
  email: string,
  birthDate: string,
  acceptedTerms: boolean,
  qsValues: any | null,
  scoreData: ScoreDataParams
}

interface LeadScore {
  letterFull: ScoreLetterFull | null,
  letterNormal: ScoreLetter | null,
  index: number | null,
  total: number | null
}

export interface BaseRegisterLead {
  cpf: string,
  birthDate: Date,
  qsValues: any | null,
  email: string,
  acceptedTermsAt: Date | null,
  scoreData: ScoreDataParams,
  score: LeadScore,
  limitCents: number | null,
  limitPerMonths: LoanRequestOption[] | null,
  sendTrackEventAt: Date | null,
  denied: boolean
}

export interface RegisterLead extends BaseRegisterLead {
  _id: string,
  createdAt: Date,
  updatedAt: Date
}

export interface BasePipedriveDeal {
  accountId: string,
  person: PipedrivePerson,
  stageId: number,
  pipedriveUserId: number,
  dealData: any,
  canBeCreate: boolean,
  hasDealGained: boolean
}

export interface PipedriveDeal extends BasePipedriveDeal {
  _id: string
}

export type DbBasePipedriveDeal = DbType<BasePipedriveDeal, 'accountId'>
export type DbPipedriveDeal = DbType<PipedriveDeal, '_id' | 'accountId'>

export interface BaseProposalPipedriveDeal {
  proposalId: string,
  dealId: number | null,
  lastStageChangeAt: Date,
  createdAt: Date,
  updatedAt: Date
}

export interface ProposalPipedriveDeal extends BaseProposalPipedriveDeal {
  _id: string
}

export type DbBaseProposalPipedriveDeal = DbType<BaseProposalPipedriveDeal, 'proposalId'>
export type DbProposalPipedriveDeal = DbType<ProposalPipedriveDeal, '_id' | 'proposalId'>
